import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import { BUTTON_CONST } from '@/src/constants/buttons';
import PATH from '@/src/routes/path';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { NAV_CONST } from '@/src/constants/nav';
import dayjs from 'dayjs';
import DetailSection from '@/src/components/organism/DetailSection';
import { DetailSctionRenderer } from '@/src/components/organism/DetailSection/renderer';
import useModalState from '@/src/hooks/useModalState';
import ContentsPreview from '@/src/components/molecule/ContentsPreview';
import {
  deleteChallengeAPI,
  getChallengeByIdAPI,
} from '@/src/apis/rest/challenge';
import { formFields } from '@/src/pages/Challenge/field';
import { UNIT_STRINGS } from '@/src/constants/unit';
import React from 'react';
import ChallengeParticipantsModal from '@/src/pages/Challenge/components/ChallengeParticipantsModal';
import { OPTIONS_CONST } from '@/src/constants/options';
import InfoSectionCard, {
  InfoData,
} from '@/components/organism/InfoSectionCard/InfoSectionCard';
import SectionCard from '@/src/components/molecule/SectionCard';
import AlertDialog from '@/src/components/atom/AlertDialog';
import { toast } from '@/src/hooks/useToast';
import { TOAST_CONST } from '@/src/constants/toast';

const LIST_PATH = PATH.CHALLENGE_LIST;
const BACK_PATH = PATH.CHALLENGE_LIST;

type ModalState = 'NONE' | 'PARTICIPANT' | 'DELETE';

export default function ChallengeDetailPage() {
  // util
  const navigate = useNavigate();
  const { id } = useParams();

  // query
  const { data: detailData } = useQuery({
    queryKey: ['challenge-detail', id],
    queryFn: () => getChallengeByIdAPI(Number(id)),
    meta: {
      backUrl: BACK_PATH,
      callback: () => {
        navigate(BACK_PATH, { replace: true });
      },
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteChallengeAPI,
  });

  const { modalState, handleToggleModal, handleOpenModal, handleCloseModal } =
    useModalState<ModalState>();

  // event
  const handleGoBack = () => {
    navigate(-1);
  };
  const handleDelete = () => {
    deleteMutation.mutate(Number(id), {
      onSuccess: () => {
        toast({
          variant: 'success',
          title: TOAST_CONST.deleteSuccess.title,
          description: TOAST_CONST.deleteSuccess.description,
        });
        setTimeout(() => {
          navigate(LIST_PATH, { replace: true });
        }, 0);
      },
      onError: e => {
        toast({
          variant: 'error',
          title: TOAST_CONST.error.title,
          description: e.message,
        });
      },
    });
  };

  // render
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={handleGoBack}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>

          <div className="flex gap-3">
            <Button
              className="w-40"
              buttonSize="medium"
              buttonColor="red"
              onClick={() => handleOpenModal('DELETE')}
              isLoading={deleteMutation.isPending}
            >
              <div className="flex items-center gap-x-2">
                {BUTTON_CONST.delete}
              </div>
            </Button>
            <Link to={PATH.CHALLENGE_EDIT.replace(':id', String(id))}>
              <Button className="w-40" buttonSize="medium">
                <div className="flex items-center gap-x-2">
                  {BUTTON_CONST.edit}
                </div>
              </Button>
            </Link>
          </div>
        </div>
      </BottomFixedContainer>
    );
  };

  return (
    <>
      <MainContentTitle
        breadcrumbList={[
          NAV_CONST.challengeManagement,
          NAV_CONST.challengeDetail,
        ]}
        pageTitle={NAV_CONST.challengeDetail}
      />
      <div className="relative mb-[100px] grid grid-cols-[1fr_1fr] gap-4">
        <DetailSection
          className="col-span-2"
          sectionTitle={
            <div className="flex w-full items-center justify-between">
              <span className="font-pretendardSemiBold text-b18sb">
                챌린지 기본 정보
              </span>
            </div>
          }
          data={detailData}
          contents={[
            [
              {
                key: formFields.startedDateRange.name,
                lable: '참여 기간',
                valueSetter: data =>
                  `${dayjs(data?.startedAt).format(
                    'YYYY-MM-DD',
                  )} ~ ${dayjs(data?.endedAt).format('YYYY-MM-DD')}`,
              },
              {
                key: formFields.isExposed.name,
                lable: '노출 여부',
                valueSetter: data => (data?.isExposed ? 'true' : 'false'),
                render: DetailSctionRenderer.BADGE({
                  options: [
                    {
                      label: OPTIONS_CONST.exposed,
                      key: 'true',
                      badgeColor: 'lightGreen',
                    },
                    {
                      label: OPTIONS_CONST.notExposed,
                      key: 'false',
                      badgeColor: 'lightGray',
                    },
                  ],
                }),
              },
              {
                key: formFields.priorityDisplayYn.name,
                lable: '우선 노출 여부',
                valueSetter: data =>
                  data?.priorityDisplayYn ? 'true' : 'false',
                render: DetailSctionRenderer.BADGE({
                  options: [
                    {
                      label: OPTIONS_CONST.applied,
                      key: 'true',
                      badgeColor: 'lightGreen',
                    },
                    {
                      label: OPTIONS_CONST.notApplied,
                      key: 'false',
                      badgeColor: 'lightGray',
                    },
                  ],
                }),
              },
              {
                key: 'thumbnailImage',
                lable: '썸네일 이미지',
                valueSetter: data => data?.thumbnailImage?.bufferFullUrl || '',
                render: DetailSctionRenderer.IMAGE({ width: 128, height: 128 }),
              },
            ],
            [
              {
                key: 'creditTemplate',
                lable: '지급 크레딧',
                valueSetter: data => {
                  const creditName = data?.creditTemplate?.title || '';
                  const credit = DetailSctionRenderer.NUMBER()(
                    typeof data?.creditTemplate?.credit === 'number'
                      ? String(data?.creditTemplate?.credit)
                      : '0',
                  );
                  return `[챌린지] ${creditName} (${credit}${UNIT_STRINGS.CREDIT})`;
                },
              },
              {
                key: 'participantsCnt',
                lable: '참여자 수',
                render: value => {
                  return (
                    <div className="flex gap-2">
                      <p className="text-b16r text-gray6">
                        {(!isNaN(Number(value))
                          ? Number(value)
                          : 0
                        ).toLocaleString()}
                        명
                      </p>
                      {!isNaN(Number(value)) && Number(value) > 0 && (
                        <Button
                          buttonSize="xSmall"
                          buttonColor="secondary"
                          onClick={() => handleOpenModal('PARTICIPANT')}
                        >
                          참여한 회원 목록 보기
                        </Button>
                      )}
                    </div>
                  );
                },
              },
              {
                key: 'approveExampleImage',
                lable: '승인 예시 이미지',
                valueSetter: data =>
                  data?.approveExampleImage?.bufferFullUrl || '',
                render: DetailSctionRenderer.IMAGE({ width: 128, height: 128 }),
              },
              {
                key: 'rejectExampleImage',
                lable: '거절 예시 이미지',
                valueSetter: data =>
                  data?.rejectExampleImage?.bufferFullUrl || '',
                render: DetailSctionRenderer.IMAGE({ width: 128, height: 128 }),
              },
              {
                key: 'createdAt',
                lable: '등록 일시',
                render: DetailSctionRenderer.DATE({
                  format: 'YYYY-MM-DD HH:mm:ss',
                }),
              },
              {
                key: ['administrator', 'name'],
                lable: '등록 관리자',
              },
            ],
          ]}
        />
        <SectionCard
          className="col-span-2"
          cardTitle={
            <div className="flex w-full items-center justify-between">
              <span className="font-pretendardSemiBold text-b18sb">
                챌린지 내용
              </span>
            </div>
          }
        >
          <div className="flex">
            <ContentsPreview
              title={detailData?.title}
              contents={detailData?.content}
            />
          </div>
        </SectionCard>
        <InfoSectionCard
          infoData={(detailData?.challengeInfos as InfoData[]) || []}
          cardTitle="안내사항 영역"
        />
        <InfoSectionCard
          infoData={(detailData?.challengeCautions as InfoData[]) || []}
          cardTitle="유의사항 영역"
        />
        {renderBottomFixedContent()}
      </div>

      {/* 모달 */}
      {modalState === 'PARTICIPANT' && (
        <ChallengeParticipantsModal
          challengeId={Number(id)}
          contentStartedAt={detailData?.startedAt}
          contentEndedAt={detailData?.endedAt}
          open={modalState === 'PARTICIPANT'}
          onOpenChange={() => handleToggleModal('PARTICIPANT')}
        />
      )}
      {modalState === 'DELETE' && (
        <AlertDialog
          open={modalState === 'DELETE'}
          onOpenChange={() => handleToggleModal('DELETE')}
          okText={BUTTON_CONST.ok}
          onOk={handleDelete}
          title="챌린지 삭제"
          okButtonProps={{
            isLoading: deleteMutation.isPending,
          }}
        >
          <p className="whitespace-pre-wrap">
            챌린지를 삭제하시겠습니까?
            <br />
            삭제된 챌린지는 사용자에게 노출되지 않습니다.
          </p>
        </AlertDialog>
      )}
    </>
  );
}
